import type { SanityImageSource } from '@sanity/image-url';
import type { PortableTextBlock } from '@portabletext/react';

export type Article = {
  _id: string;
  title: string;
  slug: string;
  mainImage: SanityImageSource | null;
  /** The main image's authored alt text (`mainImage.alt`), projected flat. */
  mainImageAlt: string | null;
  publishedAt: string | null;
  /**
   * City ids the article covers — the same codes as `cities` in
   * the app's src/lib/events-data.ts (the Studio's list lives in the content repo's
   * schemaTypes/taxonomy.ts; keep the two in sync). On the card projection too:
   * an article page picks its "more articles" by shared cities/topics.
   */
  cities: string[] | null;
  /** Topic codes, main topic first — labels in `articles.topics` (i18n). */
  topics: string[] | null;
};

// A sibling translation of this article, resolved from its
// `translation.metadata` group (see @sanity/document-internationalization in
// the Studio repo). `locale`/`slug` name the sibling's own localized URL —
// articles don't share slugs across languages the way events/tours do.
export type ArticleTranslation = { locale: string; slug: string };

export type ArticleDetail = Article & {
  body: PortableTextBlock[] | null;
  /** Optional <title> override; falls back to `title`. */
  seoTitle: string | null;
  /** Optional meta description; falls back to the start of `body`. */
  seoDescription: string | null;
  /**
   * Sanity's own document timestamps, feeding schema.org `datePublished` /
   * `dateModified`. `_createdAt` is the fallback for documents that never got
   * an explicit `publishedAt` in the CMS — `datePublished` is a required
   * Article field, so omitting it disqualifies the rich result.
   * Only the two detail projections below select these; `articlesQuery` (which
   * backs the cards) doesn't, so they live here rather than on `Article`.
   */
  _createdAt: string | null;
  _updatedAt: string | null;
  /**
   * Other-locale versions of this article, incl. itself, per the Studio's
   * `translation.metadata` group. Null when the document isn't in a group yet
   * (no metadata doc references it) or a sibling reference doesn't resolve
   * (e.g. it's still an unpublished draft).
   */
  translations: ArticleTranslation[] | null;
};

// Correlated subquery: finds the translation.metadata document (from
// @sanity/document-internationalization) that references this article, and
// resolves each of its language entries to that sibling's own locale + slug.
// `^._id` is this article's id in the outer projection's scope.
const translationsProjection = `"translations": *[
  _type == "translation.metadata" && references(^._id)
][0].translations[].value->{
  "locale": language,
  "slug": slug.current
}`;

// `$lang` is one of SUPPORTED_LOCALES (see @todo/core/i18n/locales) — every
// query is scoped to the caller's active locale rather than a fixed language.
export const articlesQuery = `*[
  _type == "post"
  && language == $lang
  && !(_id in path("drafts.**"))
] | order(publishedAt desc) {
  _id,
  title,
  "slug": slug.current,
  mainImage,
  "mainImageAlt": mainImage.alt,
  publishedAt,
  cities,
  topics
}`;

// Every article with body, for one locale — used by generateStaticParams to
// pre-render each article page and bake its meta + content into the static HTML.
export const allArticlesDetailQuery = `*[
  _type == "post"
  && language == $lang
  && !(_id in path("drafts.**"))
] | order(publishedAt desc) {
  _id,
  title,
  "slug": slug.current,
  mainImage,
  "mainImageAlt": mainImage.alt,
  publishedAt,
  _createdAt,
  _updatedAt,
  body,
  seoTitle,
  seoDescription,
  cities,
  topics,
  ${translationsProjection}
}`;

export const articleBySlugQuery = `*[
  _type == "post"
  && slug.current == $slug
  && language == $lang
  && !(_id in path("drafts.**"))
][0]{
  _id,
  title,
  "slug": slug.current,
  mainImage,
  "mainImageAlt": mainImage.alt,
  publishedAt,
  _createdAt,
  _updatedAt,
  body,
  seoTitle,
  seoDescription,
  cities,
  topics,
  ${translationsProjection}
}`;
